<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Relationship Classification Rule</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row >
            <v-col cols="12">
              <v-autocomplete label="Relationship Type"
                :items="relationshipClassifications"
                item-text="text"
                item-value="key"
                v-model="workingRule.relationshipClassification"
                @change="onModification"
                :loading="loading"/>
            </v-col>

            <v-col cols="12">
              <v-combobox label = "Codes"
                id="codes"
                multiple
                return-object
                v-model="workingRule.relationshipCodes"
                append-icon
                chips
                deletable-chips
                :rules="requiredRule"
                :search-input.sync="enteredCode"
                @update:search-input="splitIntoCodes"
                @change="onModification">
              </v-combobox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="cancelButton" large @click="cancel" :disabled="saving">Cancel</v-btn>
          <v-btn id="saveButton" large class="blue white--text" @click="save"
            :loading="saving" :disabled="!isModificationSavable">
              Save
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { RelationshipClassificationRuleResource, RelationshipClassificationRule }
  from '@/api/relationshipclassification/';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';

@Component({ computed: mapState(['fiId']) })
export default class RelationshipClassificationRuleDialog extends Vue {
  @Inject('RelationshipClassificationRuleResource')
  ruleResource!:RelationshipClassificationRuleResource;

  @Prop()
  rule!:RelationshipClassificationRule;

  @Prop({ default: [] })
  existingRules!:Array<RelationshipClassificationRule>;

  @Prop()
  isEditButton!:boolean;

  @Prop()
  classificationMap!:Map<string, string>;

  workingRule:RelationshipClassificationRule =
    RelationshipClassificationRuleDialog.createEmptyRule();

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  enteredCode:string = '';

  relationshipClassifications:{ [key: string]: string }[] = [];

  isModificationSavable:boolean = false;

  loading = false;

  delimiter: string = ',';

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  static createEmptyRule() : RelationshipClassificationRule {
    return {
      id: undefined,
      relationshipClassification: '',
      relationshipCodes: [],
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingRule();
      if (this.relationshipClassifications.length === 0
        && this.classificationMap !== undefined) {
        this.buildClassificationsFromMap();
      }
      this.loading = false;
    }
  }

  initializeWorkingRule() {
    this.isModificationSavable = false;
    if (this.rule) {
      Object.assign(this.workingRule, this.rule);
    } else {
      this.workingRule = RelationshipClassificationRuleDialog.createEmptyRule();
    }
  }

  buildClassificationsFromMap() {
    this.classificationMap.forEach((value: string, key: string) => {
      const text:string = value;
      this.relationshipClassifications.push({
        key,
        text,
      });
    });
  }

  cancel() {
    this.initializeWorkingRule();
    this.dialog = false;
  }

  save() {
    // Force combos to update because they might still have focus when user clicks save.
    if (this.enteredCode && this.enteredCode.trim()
      && !this.workingRule.relationshipCodes.includes(this.enteredCode.trim())) {
      this.workingRule.relationshipCodes.push(this.enteredCode.trim());
    }

    this.saving = true;
    if (!this.areWorkingCodesUnique()) {
      this.saving = false;
      this.setError('Duplicate Relationship Code!');
      return;
    }
    if (!this.workingRule.relationshipClassification) {
      this.saving = false;
      this.setError('Select a Relationship Type!');
      return;
    }
    this.ruleResource.save(this.fiId, this.workingRule)
      .then((savedRule:RelationshipClassificationRule) => {
        if (this.rule) {
          Object.assign(this.rule, savedRule);
        }
        this.$emit('success', savedRule);
        this.dialog = false;
        this.initializeWorkingRule();
      })
      .catch((error:any) => {
        this.saving = false;
        this.showError();
      });
  }

  areWorkingCodesUnique(): boolean {
    const allCodes = new Set<string>();
    this.existingRules.forEach((rule) => {
      if (rule.id !== this.workingRule.id) {
        rule.relationshipCodes.forEach((code) => {
          allCodes.add(code);
        });
      }
    });
    let isUnique = true;
    this.workingRule.relationshipCodes.forEach((code) => {
      if (allCodes.has(code)) {
        isUnique = false;
      }
    });
    return isUnique;
  }

  splitIntoCodes() {
    if (this.enteredCode && this.enteredCode.split(this.delimiter).length > 1) {
      this.workingRule.relationshipCodes.push(...this.enteredCode
        .split(this.delimiter).filter(term => !this.workingRule.relationshipCodes.includes(term) && term.trim() !== ''));
      this.enteredCode = '';
      this.onModification();
    }
  }

  onModification() {
    const oldCodes = new Set<string>();
    if (this.rule) {
      this.rule.relationshipCodes.forEach((code) => {
        oldCodes.add(code);
      });
    }
    const newCodes = new Set<string>();
    if (this.workingRule && this.workingRule.relationshipCodes) {
      this.workingRule.relationshipCodes.forEach((code) => {
        newCodes.add(code);
      });
    }
    const deletedCodes = new Set([...oldCodes].filter(x => !newCodes.has(x)));
    const addedCodes = new Set([...newCodes].filter(x => !oldCodes.has(x)));

    this.isModificationSavable = this.workingRule.relationshipClassification !== '' // type selected
      && this.workingRule.relationshipCodes.length > 0 // non empty code
      && (deletedCodes.size > 0 // code removed
          || addedCodes.size > 0 // code added
          || (this.rule
              && this.rule.relationshipClassification // type modified
              !== this.workingRule.relationshipClassification));
  }

  showError() {
    const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  setError(message:string) {
	  const al = new Alert(message, NoticeClass.ERROR);
	  al.setTimeout(5000);
	  this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
